import { useState, useMemo } from 'react';
import { useCompanies } from '../hooks/useCompanies';
import { useToast } from '../hooks/useToast';
import CompanyModal from '../components/companies/CompanyModal';
import LoadingSpinner from '../components/common/LoadingSpinner';
import EmptyState from '../components/common/EmptyState';
import { ToastContainer } from '../components/common/Toast';
import { getInitials } from '../utils/formatters';

const Companies = () => {
  const { companies, loading, createCompany, updateCompany, deleteCompany } = useCompanies();
  const toast = useToast();

  const [searchQuery, setSearchQuery] = useState('');
  const [industryFilter, setIndustryFilter] = useState('All');
  const [selectedLetter, setSelectedLetter] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);

  const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

  const industries = useMemo(() => {
    const uniqueIndustries = [...new Set(companies.map(c => c.industry).filter(Boolean))];
    return uniqueIndustries.sort();
  }, [companies]);

  const filteredCompanies = useMemo(() => {
    let result = companies;

    if (industryFilter !== 'All') {
      result = result.filter(c => c.industry === industryFilter);
    }

    if (selectedLetter) {
      result = result.filter(c => c.name?.toUpperCase().startsWith(selectedLetter));
    }

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(c =>
        c.name?.toLowerCase().includes(query) ||
        c.displayName?.toLowerCase().includes(query) ||
        c.industry?.toLowerCase().includes(query) ||
        c.website?.toLowerCase().includes(query)
      );
    }

    return result;
  }, [companies, industryFilter, selectedLetter, searchQuery]);

  const handleAddCompany = () => {
    setEditingCompany(null);
    setIsModalOpen(true);
  };

  const handleEditCompany = (company) => {
    setEditingCompany(company);
    setIsModalOpen(true);
  };

  const handleDeleteCompany = async (company) => {
    const confirmMessage = `Are you sure you want to delete "${company.displayName || company.name}"?\n\nThis action cannot be undone. The company can only be deleted if it has no associated deals or contacts.`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      await deleteCompany(company.id);
      toast.success('Company deleted successfully');
    } catch (error) {
      const errorMessage = error.message || 'Failed to delete company';
      toast.error(errorMessage);
    }
  };

  const handleSubmitCompany = async (formData) => {
    setModalLoading(true);
    try {
      if (editingCompany) {
        await updateCompany(editingCompany.id, formData);
        toast.success('Company updated successfully');
      } else {
        await createCompany(formData);
        toast.success('Company created successfully');
      }
      setIsModalOpen(false);
      setEditingCompany(null);
    } catch (error) {
      toast.error(error.message || 'Failed to save company');
    } finally {
      setModalLoading(false);
    }
  };

  const formatRevenue = (revenue) => {
    if (!revenue) return '-';
    if (revenue >= 1000000) {
      return `€${(revenue / 1000000).toFixed(1)}M`;
    }
    if (revenue >= 1000) {
      return `€${(revenue / 1000).toFixed(0)}K`;
    }
    return `€${revenue}`;
  };

  const exportToCSV = () => {
    if (companies.length === 0) {
      toast.warning('No companies to export');
      return;
    }

    const headers = ['Name', 'Industry', 'Website', 'VAT ID', 'Employees', 'Revenue', 'City', 'Country'];
    const csvContent = [
      headers.join(','),
      ...companies.map(c => [
        `"${c.name || ''}"`,
        `"${c.industry || ''}"`,
        `"${c.website || ''}"`,
        `"${c.vatId || ''}"`,
        c.employeeCount || '',
        c.annualRevenue || '',
        `"${c.address?.city || ''}"`,
        `"${c.address?.country || ''}"`
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `companies_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();

    toast.success('Companies exported successfully');
  };

  return (
    <div className="flex-1 flex flex-col relative bg-page">
      <ToastContainer toasts={toast.toasts} removeToast={toast.removeToast} />

      <CompanyModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setEditingCompany(null);
        }}
        company={editingCompany}
        onSubmit={handleSubmitCompany}
        loading={modalLoading}
      />

      {/* Header */}
      <div className="sticky -top-4 md:-top-8 px-6 py-5 border-b border-neutral-200 bg-white backdrop-blur-sm z-[200]">
        <div className="max-w-[1200px] mx-auto w-full flex flex-col gap-5">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div className="flex flex-col gap-1">
              <h1 className="font-display text-3xl text-neutral-900 tracking-tight">
                Company Directory
              </h1>
              <p className="text-neutral-500 text-sm">
                Manage your business relationships ({companies.length} total)
              </p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={exportToCSV}
                disabled={companies.length === 0}
                className="flex items-center justify-center gap-2 rounded-lg h-10 px-4 bg-white border border-neutral-200 text-neutral-700 text-sm font-medium hover:bg-neutral-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <span className="material-symbols-outlined text-[20px]">file_download</span>
                <span className="truncate">Export CSV</span>
              </button>
              <button
                onClick={handleAddCompany}
                className="flex items-center justify-center gap-2 rounded-lg h-10 px-4 bg-primary text-white text-sm font-semibold hover:bg-primary-500 transition-all shadow-md shadow-primary/25 hover:-translate-y-0.5"
              >
                <span className="material-symbols-outlined text-[20px]">add</span>
                <span className="hidden sm:inline">New Company</span>
                <span className="sm:hidden">New</span>
              </button>
            </div>
          </div>

          {/* Search & Filters */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="flex w-full md:max-w-[320px] items-center rounded-lg bg-white border border-neutral-200 h-10 focus-within:ring-2 focus-within:ring-primary-200 focus-within:border-primary transition-all">
              <div className="pl-3 pr-2 text-neutral-400 flex items-center justify-center">
                <span className="material-symbols-outlined text-[20px]">search</span>
              </div>
              <input
                className="w-full bg-transparent border-none text-sm text-neutral-900 placeholder-neutral-400 focus:ring-0 px-0 h-full outline-none"
                placeholder="Search companies..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setSelectedLetter(null);
                }}
              />
            </div>

            <div className="flex gap-2 overflow-x-auto pb-1 md:pb-0 scrollbar-hide">
              <button
                onClick={() => setIndustryFilter('All')}
                className={`flex h-8 shrink-0 items-center justify-center gap-x-2 rounded-lg px-3 transition-colors ${
                  industryFilter === 'All'
                    ? 'bg-neutral-200 text-neutral-800'
                    : 'bg-white border border-neutral-200 text-neutral-600 hover:bg-neutral-50'
                }`}
              >
                <span className="material-symbols-outlined text-[18px]">list</span>
                <p className="text-sm font-medium">All Industries</p>
              </button>
              {industries.slice(0, 4).map((industry) => (
                <button
                  key={industry}
                  onClick={() => setIndustryFilter(industry)}
                  className={`flex h-8 shrink-0 items-center justify-center gap-x-2 rounded-lg px-3 transition-colors ${
                    industryFilter === industry
                      ? 'bg-primary-100 border border-primary-200 text-primary-700'
                      : 'bg-white border border-neutral-200 text-neutral-600 hover:bg-neutral-50'
                  }`}
                >
                  <span className="material-symbols-outlined text-[18px] text-primary">business</span>
                  <p className="text-sm font-medium">{industry}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Alphabet Navigation */}
          <div className="flex flex-wrap gap-1 items-center">
            <button
              onClick={() => setSelectedLetter(null)}
              className={`h-7 px-2.5 rounded text-xs font-semibold transition-colors ${
                selectedLetter === null
                  ? 'bg-primary text-white'
                  : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'
              }`}
            >
              All
            </button>
            {alphabet.map((letter) => {
              const hasCompanies = companies.some(c => c.name?.toUpperCase().startsWith(letter));
              return (
                <button
                  key={letter}
                  onClick={() => setSelectedLetter(letter)}
                  disabled={!hasCompanies}
                  className={`h-7 w-7 rounded text-xs font-semibold transition-colors ${
                    selectedLetter === letter
                      ? 'bg-primary text-white'
                      : hasCompanies
                      ? 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'
                      : 'bg-neutral-50 text-neutral-300 cursor-not-allowed'
                  }`}
                >
                  {letter}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Table Content */}
      <div className="flex-1 overflow-y-auto p-4 md:p-6 scrollbar-thin">
        <div className="max-w-[1200px] mx-auto w-full">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <LoadingSpinner size="lg" message="Loading companies..." />
            </div>
          ) : filteredCompanies.length === 0 ? (
            <EmptyState
              icon={searchQuery || industryFilter !== 'All' ? 'search_off' : 'business'}
              title={searchQuery || industryFilter !== 'All' ? 'No companies found' : 'No companies yet'}
              description={searchQuery || industryFilter !== 'All' ? 'Try adjusting your search or filters' : 'Get started by adding your first company'}
              actionLabel={!searchQuery && industryFilter === 'All' ? 'Add Company' : null}
              onAction={!searchQuery && industryFilter === 'All' ? handleAddCompany : null}
            />
          ) : (
            <>
              <div className="flex overflow-hidden rounded-xl border border-neutral-200 bg-white shadow-sm">
                <table className="flex-1 w-full min-w-[800px]">
                  <thead className="bg-neutral-50 border-b border-neutral-200">
                    <tr>
                      <th className="px-6 py-4 text-left w-[280px] text-xs font-semibold uppercase tracking-wider text-neutral-500">
                        Company
                      </th>
                      <th className="px-6 py-4 text-left w-[160px] text-xs font-semibold uppercase tracking-wider text-neutral-500 hidden md:table-cell">
                        Industry
                      </th>
                      <th className="px-6 py-4 text-left w-[140px] text-xs font-semibold uppercase tracking-wider text-neutral-500 hidden lg:table-cell">
                        Employees
                      </th>
                      <th className="px-6 py-4 text-left w-[140px] text-xs font-semibold uppercase tracking-wider text-neutral-500 hidden xl:table-cell">
                        Revenue
                      </th>
                      <th className="px-6 py-4 text-right w-[80px] text-xs font-semibold uppercase tracking-wider text-neutral-500">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-neutral-100">
                    {filteredCompanies.map((company) => (
                      <tr
                        key={company.id}
                        className="hover:bg-neutral-50 transition-colors group cursor-pointer"
                        onClick={() => handleEditCompany(company)}
                      >
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            {company.logoUrl ? (
                              <img
                                src={company.logoUrl}
                                alt={company.name}
                                className="size-10 rounded-lg object-cover border border-neutral-200"
                              />
                            ) : (
                              <div className="size-10 rounded-lg bg-gradient-to-br from-primary-400 to-primary-600 flex items-center justify-center text-white font-semibold text-sm shadow-sm">
                                {getInitials(company.name)}
                              </div>
                            )}
                            <div className="flex flex-col">
                              <p className="text-neutral-900 text-sm font-semibold">{company.displayName || company.name}</p>
                              {company.website && (
                                <a
                                  href={company.website}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  onClick={(e) => e.stopPropagation()}
                                  className="text-neutral-500 hover:text-primary text-xs flex items-center gap-1"
                                >
                                  <span className="material-symbols-outlined text-[14px]">link</span>
                                  {company.website.replace(/^https?:\/\//, '').replace(/\/$/, '')}
                                </a>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 hidden md:table-cell">
                          {company.industry ? (
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-primary-100 text-primary-700">
                              <span className="material-symbols-outlined text-[14px]">business</span>
                              {company.industry}
                            </span>
                          ) : (
                            <span className="text-neutral-400 text-sm">-</span>
                          )}
                        </td>
                        <td className="px-6 py-4 hidden lg:table-cell">
                          <p className="text-neutral-900 text-sm">
                            {company.employeeCount ? `${company.employeeCount} employees` : '-'}
                          </p>
                        </td>
                        <td className="px-6 py-4 hidden xl:table-cell">
                          <p className="text-neutral-900 text-sm font-medium">
                            {formatRevenue(company.annualRevenue)}
                          </p>
                        </td>
                        <td className="px-6 py-4 text-right">
                          <div className="flex items-center justify-end gap-1">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEditCompany(company);
                              }}
                              className="text-neutral-400 hover:text-primary transition-colors p-1.5 rounded-lg hover:bg-neutral-100"
                              title="Edit company"
                            >
                              <span className="material-symbols-outlined text-[18px]">edit</span>
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteCompany(company);
                              }}
                              className="text-neutral-400 hover:text-error transition-colors p-1.5 rounded-lg hover:bg-error-light"
                              title="Delete company"
                            >
                              <span className="material-symbols-outlined text-[18px]">delete</span>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="flex items-center justify-between px-2 py-4">
                <p className="text-xs text-neutral-500">
                  Showing {filteredCompanies.length} of {companies.length} companies
                </p>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Companies;
